import {
  Activity,
  Calendar,
  CalendarDays,
  ClipboardList,
  Filter,
  LayoutDashboard,
  Mail,
  Star,
  Tag,
  Users,
  type LucideIcon,
} from "lucide-react";
import { NavLink } from "react-router-dom";

import logoMark from "@/assets/logo-mark.svg";
import { useAuth } from "@/auth/AuthContext";
import { useReviewQueue } from "@/hooks/useReviewQueue";
import { useDashboardStats } from "@/hooks/useDashboardStats";
import { cn } from "@/lib/utils";

type BadgeKind = "review" | "unrated";

interface NavItem {
  label: string;
  icon: LucideIcon;
  to?: string;
  comingSoon?: boolean;
  badge?: BadgeKind;
}

const ADMIN_ITEMS: NavItem[] = [
  { label: "Utenti", icon: Users, to: "/admin/users" },
  { label: "Mailbox", icon: Mail, to: "/admin/mailboxes" },
  { label: "Monitoraggio", icon: Activity, to: "/admin/monitoring" },
];

const ITEMS: NavItem[] = [
  { label: "Eventi", icon: Calendar, to: "/events" },
  { label: "Calendario", icon: CalendarDays, to: "/calendar" },
  {
    label: "Coda revisione",
    icon: ClipboardList,
    to: "/review",
    badge: "review",
  },
  {
    label: "Review priorità",
    icon: Star,
    to: "/priority-review",
    badge: "unrated",
  },
  { label: "Dashboard", icon: LayoutDashboard, to: "/dashboard" },
  { label: "Categorie", icon: Tag, to: "/categories" },
  { label: "Filtri email", icon: Filter, to: "/filters" },
];

interface SidebarNavContentProps {
  /** Called when a nav link is clicked (e.g. to close the mobile drawer). */
  onNavigate?: () => void;
}

export function SidebarNavContent({ onNavigate }: SidebarNavContentProps) {
  const reviewQueue = useReviewQueue();
  const reviewCount = reviewQueue.data?.total ?? 0;
  const stats = useDashboardStats();
  const unratedCount = stats.data?.priorityStats?.unrated ?? 0;
  const { user } = useAuth();
  const isAdmin = (user as { role?: string } | null)?.role === "admin";

  return (
    <>
      <nav className="flex-1 space-y-1 p-3">
        {ITEMS.map((item) =>
          renderNavItem(item, reviewCount, unratedCount, onNavigate),
        )}
        {isAdmin ? (
          <>
            <div className="mb-1 mt-5 px-3 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">
              Amministrazione
            </div>
            {ADMIN_ITEMS.map((item) =>
              renderNavItem(item, reviewCount, unratedCount, onNavigate),
            )}
          </>
        ) : null}
      </nav>
      <div className="border-t border-white/50 px-5 py-3 text-[10px] uppercase tracking-[0.18em] text-slate-400">
        Sistema interno
      </div>
    </>
  );
}

export function AppSidebar() {
  return (
    <aside className="glass-chrome relative z-10 hidden w-64 shrink-0 border-r border-white/50 md:flex md:flex-col">
      <div className="flex h-16 items-center gap-3 border-b border-white/50 px-5">
        <img
          src={logoMark}
          alt=""
          aria-hidden="true"
          className="h-9 w-9 shrink-0 drop-shadow-[0_8px_20px_-10px_rgba(37,99,235,0.85)]"
        />
        <div className="leading-tight">
          <div className="font-display bg-gradient-to-br from-slate-900 to-blue-700 bg-clip-text text-lg font-semibold tracking-tight text-transparent">
            Nuntius
          </div>
          <div className="text-[10px] font-medium uppercase tracking-[0.2em] text-slate-400">
            Corriere del Ticino
          </div>
        </div>
      </div>
      <SidebarNavContent />
    </aside>
  );
}

function renderNavItem(
  item: NavItem,
  reviewCount: number,
  unratedCount: number,
  onNavigate?: () => void,
) {
  const Icon = item.icon;
  if (item.to) {
    return (
      <NavLink
        key={item.label}
        to={item.to}
        onClick={onNavigate}
        className={({ isActive }) =>
          cn(
            "group flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-all",
            isActive
              ? "bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-[0_10px_24px_-10px_rgba(37,99,235,0.8)]"
              : "text-slate-600 hover:bg-white/70 hover:text-slate-900",
          )
        }
      >
        {({ isActive }) => (
          <>
            <Icon className="h-4 w-4" />
            <span className="flex-1">{item.label}</span>
            {item.badge === "review" && reviewCount > 0 ? (
              <ReviewBadge count={reviewCount} active={isActive} />
            ) : null}
            {item.badge === "unrated" && unratedCount > 0 ? (
              <ReviewBadge count={unratedCount} active={isActive} />
            ) : null}
          </>
        )}
      </NavLink>
    );
  }
  return (
    <span
      key={item.label}
      className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-slate-400"
      title="In arrivo"
    >
      <Icon className="h-4 w-4" />
      <span className="flex-1">{item.label}</span>
      <span className="rounded-full bg-slate-200/70 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide">
        soon
      </span>
    </span>
  );
}

interface ReviewBadgeProps {
  count: number;
  active: boolean;
}

function ReviewBadge({ count, active }: ReviewBadgeProps) {
  const label = count > 99 ? "99+" : String(count);
  return (
    <span
      aria-label={`${count} eventi da revisionare`}
      className={cn(
        "inline-flex min-w-[1.5rem] items-center justify-center rounded-full px-1.5 py-0.5 text-[11px] font-semibold tabular-nums",
        active
          ? "bg-primary-foreground/20 text-primary-foreground"
          : "bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200",
      )}
    >
      {label}
    </span>
  );
}
